import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faXmark,
  faRightFromBracket,
  faChalkboardTeacher,
  faCalendarAlt,
  faUserGraduate,
  faBookOpen,
  faMoneyBillWave,
  faTrophy,
  faMedal,
  faGear,
  faSun,
  faMoon,
  faStickyNote,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import NotificationBell from '../Admin/NotificationBell'; // same bell as the admin dashboard
import './Layout.css';

Modal.setAppElement('#root');

const API = process.env.REACT_APP_API_BASE_URL;
const LOGO_DARK = '/assets/img/logo/logowhitenew.png';
const LOGO_LIGHT = '/assets/img/logo/logonew.png';
const THEME_KEY = 'rm-demo-theme'; // same key as the other dashboards

// old reminder flags, still cleared at logout
const FLAG_AVAIL = 'availability_notification_shown';
const FLAG_NOTE = 'note_notification_shown';
const FLAG_PROMPT = 'note_prompt_notification_shown';

const NAV = [
  { to: '/teacher/Dashboard', icon: faHome, label: 'Dashboard' },
  { to: '/teacher/MyClasses', icon: faChalkboardTeacher, label: 'My Classes' },
  { to: '/teacher/MySchedule', icon: faCalendarAlt, label: 'My Schedule' },
  { to: '/teacher/MyStudents', icon: faUserGraduate, label: 'My Students', also: '/StudentProfile' },
  { to: '/teacher/Curriculum', icon: faBookOpen, label: 'Curriculum' },
  { to: '/teacher/competitions', icon: faTrophy, label: 'Competitions' },
  { to: '/teacher/badges', icon: faMedal, label: 'Badges' },
  { to: '/teacher/MyEarnings', icon: faMoneyBillWave, label: 'My Earnings' },
  { to: '/teacher/Settings', icon: faGear, label: 'Settings' },
];

const safe = {
  get: (store, key) => {
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  },
  set: (store, key, value) => {
    try {
      store.setItem(key, value);
    } catch {
      /* private mode */
    }
  },
  remove: (store, key) => {
    try {
      store.removeItem(key);
    } catch {
      /* private mode */
    }
  },
};

const readToken = () => {
  try {
    const raw = localStorage.getItem('userData');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const TeacherLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useState(readToken); // read once, stable between renders
  const [compCount, setCompCount] = useState(0); // challenges open / coming / to score (menu badge)

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    // challenges open or coming, plus the ones waiting for my score
    fetch(`${API}/api/teacher/competitions`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        const list = d.competitions || [];
        setCompCount(list.filter((c) => c.status === 'open' || c.status === 'upcoming' || (c.status === 'judging' && c.toScore > 0)).length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [theme, setTheme] = useState(() => (safe.get(localStorage, THEME_KEY) === 'dark' ? 'dark' : 'light'));
  const [userData, setUserData] = useState(null);

  const [note, setNote] = useState('');

  // Note editor
  const [noteOpen, setNoteOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [noteError, setNoteError] = useState('');

  const isLight = theme === 'light';
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;

  // ---------- Teacher profile (same endpoint) ----------
  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API}/api/teacher/layout`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Unauthorized');
        const data = await response.json();
        if (!cancelled) {
          setUserData({
            id: data.id,
            username: data.username,
            firstName: data.firstName,
            lastName: data.lastName,
            Image: data.Image,
            fullName: [data.firstName, data.lastName].filter(Boolean).join(' '),
          });
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        if (!cancelled) navigate('/login', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  // ---------- Heartbeat (same endpoint), now and every 30s ----------
  useEffect(() => {
    if (!token) return undefined;
    const sendHeartbeat = () =>
      fetch(`${API}/api/auth/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        // Same body as before (the old code sent IdStudent: undefined, i.e. an empty body);
        // the server identifies the teacher from the token.
        body: JSON.stringify({}),
      }).catch((error) => console.error('Heartbeat failed:', error));
    sendHeartbeat();
    const intervalId = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(intervalId);
  }, [token]);

  // ---------- Personal note (same endpoint): only for the dot on the Note button ----------
  // (the pop-up reminders for availability and notes were removed)
  const teacherId = userData?.id;
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/api/teacher/layout/note`, auth)
      .then((res) => {
        if (!cancelled) setNote(res.data?.note || '');
      })
      .catch((error) => console.error('Error fetching teacher note:', error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId]);

  // Close the mobile menu when the page changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => {
    const next = isLight ? 'dark' : 'light';
    setTheme(next);
    safe.set(localStorage, THEME_KEY, next);
  };

  const handleLogout = () => {
    // offline right away (shared laptops: the next person who logs in shows correctly)
    if (token) {
      fetch(`${API}/api/auth/verify/offline`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, keepalive: true }).catch(() => {});
    }
    [FLAG_AVAIL, FLAG_NOTE, FLAG_PROMPT].forEach((k) => safe.remove(sessionStorage, k));
    safe.remove(localStorage, 'lastLoginTimestamp');
    localStorage.removeItem('userData');
    navigate('/login', { replace: true });
  };

  // ---------- Note editor ----------
  const openNote = useCallback(() => {
    setDraft(note || '');
    setNoteError('');
    setConfirmDelete(false);
    setNoteOpen(true);
  }, [note]);

  const saveNote = async () => {
    setSavingNote(true);
    setNoteError('');
    try {
      const clean = draft.trim();
      await axios.post(`${API}/api/teacher/layout/note`, { note: clean }, auth);
      setNote(clean);
      setNoteOpen(false);
    } catch (error) {
      console.error('Error saving note:', error);
      setNoteError('We couldn’t save your note. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true); // first click asks to confirm
      return;
    }
    setDeleting(true);
    setNoteError('');
    try {
      await axios.delete(`${API}/api/teacher/layout/note`, auth);
      setNote('');
      setDraft('');
      setNoteOpen(false);
    } catch (error) {
      console.error('Error deleting note:', error);
      setNoteError('We couldn’t delete your note. Please try again.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const fullName = userData?.fullName || '';
  const initials =
    (fullName || 'T')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join('') || 'T';

  const helpUrl = `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(
    `Hi! It's ${fullName || 'a RoboMind teacher'}. I need help with the teacher dashboard.`
  )}&type=phone_number&app_absent=0`;

  // Pass userData to the page, like before
  const childrenWithProps = React.Children.map(children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, { userData }) : child
  );

  return (
    <div className="rmtl" data-theme={theme}>
      {/* ---------- Top bar ---------- */}
      <header className="rmtl-top">
        <div className="rmtl-top-left">
          <button
            type="button"
            className="rmtl-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="rmtl-side"
          >
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
          </button>
          <Link to="/teacher/Dashboard" className="rmtl-logo" aria-label="Dashboard">
            <img src={isLight ? LOGO_LIGHT : LOGO_DARK} alt="RoboMind" />
          </Link>
        </div>

        <div className="rmtl-top-right">
          <button
            type="button"
            className={`rmtl-note-btn ${note ? 'has-note' : ''}`}
            onClick={openNote}
            aria-label={note ? 'Open your note' : 'Add a note'}
          >
            <FontAwesomeIcon icon={faStickyNote} />
            <span>Note</span>
            {note && <i className="rmtl-note-dot" aria-hidden="true" />}
          </button>

          <NotificationBell base={`${API}/api/teacher/notifications`} token={token} />

          <button
            type="button"
            className="rmtl-theme-btn"
            onClick={toggleTheme}
            aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
            title={isLight ? 'Dark mode' : 'Light mode'}
          >
            <FontAwesomeIcon icon={isLight ? faMoon : faSun} />
          </button>

          <Link to="/teacher/Settings" className="rmtl-user" aria-label="Your settings">
            <span className="rmtl-avatar" aria-hidden="true">
              {userData?.Image ? <img src={userData.Image} alt="" /> : initials}
            </span>
            <span className="rmtl-user-name">{fullName || 'Teacher'}</span>
          </Link>
        </div>
      </header>

      {/* ---------- Sidebar (drawer on mobile) ---------- */}
      <aside id="rmtl-side" className={`rmtl-side ${menuOpen ? 'is-open' : ''}`} aria-label="Teacher menu">
        <nav>
          <ul className="rmtl-nav">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `rmtl-nav-item ${isActive || (item.also && location.pathname.startsWith(item.also)) ? 'is-active' : ''}`
                  }
                >
                  <FontAwesomeIcon icon={item.icon} />
                  <span>{item.label}</span>
                  {item.to === '/teacher/competitions' && compCount > 0 && (
                    <b className="rmtl-nav-badge" aria-label={`${compCount} competitions`}>{compCount}</b>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="rmtl-side-bottom">
          <div className="rmtl-help">
            <strong>Need help?</strong>
            <span>Schedule, students or payments.</span>
            <a href={helpUrl} target="_blank" rel="noopener noreferrer" className="rmtl-help-btn">
              <FontAwesomeIcon icon={faWhatsapp} /> Message admin
            </a>
          </div>

          {/* phones: light / dark lives here (the top bar is too small) */}
          <button type="button" className="rmtl-side-theme" onClick={toggleTheme}>
            <FontAwesomeIcon icon={isLight ? faMoon : faSun} />
            {isLight ? 'Dark mode' : 'Light mode'}
          </button>

          <button type="button" className="rmtl-leave" onClick={() => setShowLogout(true)}>
            <FontAwesomeIcon icon={faRightFromBracket} />
            Log out
          </button>
        </div>
      </aside>

      {menuOpen && <button type="button" className="rmtl-scrim" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}

      {/* ---------- Page ---------- */}
      <main className="rmtl-main">{childrenWithProps}</main>

      {/* ---------- Logout ---------- */}
      <Modal
        isOpen={showLogout}
        onRequestClose={() => setShowLogout(false)}
        contentLabel="Log out"
        className={`rmtl-modal ${isLight ? 'is-light' : ''}`}
        overlayClassName="rmtl-modal-overlay"
      >
        <h2 className="rmtl-modal-title">Log out?</h2>
        <p className="rmtl-modal-text">You can sign in again any time.</p>
        <div className="rmtl-modal-actions">
          <button type="button" className="rmtl-modal-cancel" onClick={() => setShowLogout(false)}>
            Stay
          </button>
          <button type="button" className="rmtl-modal-confirm" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </Modal>

      {/* ---------- Personal note ---------- */}
      <Modal
        isOpen={noteOpen}
        onRequestClose={() => !savingNote && !deleting && setNoteOpen(false)}
        contentLabel="Personal note"
        className={`rmtl-modal rmtl-note ${isLight ? 'is-light' : ''}`}
        overlayClassName="rmtl-modal-overlay"
      >
        <div className="rmtl-note-head">
          <FontAwesomeIcon icon={faStickyNote} />
          <h2 className="rmtl-modal-title">{note ? 'Your note' : 'Add a personal note'}</h2>
        </div>
        <p className="rmtl-modal-text">Only you can see it. It pops up each time you sign in.</p>

        <textarea
          id="rmtl-note-input"
          aria-label="Your note"
          className="rmtl-note-textarea"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setConfirmDelete(false);
          }}
          placeholder="e.g. Follow up with Karim’s parent, prepare Friday’s robot project…"
          rows={6}
        />
        <p className="rmtl-note-count">{draft.length} characters</p>
        {noteError && (
          <p className="rmtl-note-error" role="alert">
            {noteError}
          </p>
        )}

        <div className="rmtl-note-actions">
          {note && (
            <button
              type="button"
              className={`rmtl-note-delete ${confirmDelete ? 'is-confirm' : ''}`}
              onClick={deleteNote}
              disabled={savingNote || deleting}
            >
              {deleting ? 'Deleting…' : confirmDelete ? 'Yes, delete' : 'Delete'}
            </button>
          )}
          <button type="button" className="rmtl-note-cancel" onClick={() => setNoteOpen(false)} disabled={savingNote || deleting}>
            Cancel
          </button>
          <button type="button" className="rmtl-note-save" onClick={saveNote} disabled={savingNote || deleting}>
            {savingNote ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TeacherLayout;